import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Save, MapPin, Phone, Mail, Clock, Star, Globe, Building2 } from "lucide-react";
import { api } from "../lib/api";
import { useSettings } from "../lib/settings";
import { toast } from "sonner";

export default function AdminSettings() {
  const { settings, refresh } = useSettings();
  const [form, setForm] = useState(settings);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setForm(settings); }, [settings]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/admin/settings", form);
      await refresh();
      toast.success("Business info updated");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="page-admin-settings">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <h1 className="font-display text-5xl md:text-6xl tracking-tighter">Business Info</h1>
        <p className="text-white/50 mt-3 text-sm max-w-lg">
          What you save here updates the footer, contact page, and header instantly across the site.
        </p>
      </motion.div>

      <form onSubmit={onSubmit} className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-8" data-testid="settings-form">
        <section className="bento-cell space-y-5">
          <Header icon={Building2} label="Identity" />
          <Field label="Business name">
            <input data-testid="s-name" value={form.business_name || ""} onChange={(e) => setField("business_name", e.target.value)} />
          </Field>
          <Field label="Short tagline (footer/nav)">
            <input data-testid="s-tagline" value={form.tagline || ""} onChange={(e) => setField("tagline", e.target.value)} />
          </Field>
          <Field label="Region label (hero)">
            <input data-testid="s-region" value={form.city_region || ""} onChange={(e) => setField("city_region", e.target.value)} />
          </Field>
          <Field label={<><Star size={12} className="inline mr-1" /> Star rating (e.g. 4.6★)</>}>
            <input data-testid="s-rating" value={form.rating || ""} onChange={(e) => setField("rating", e.target.value)} />
          </Field>
        </section>

        <section className="bento-cell space-y-5">
          <Header icon={MapPin} label="Location" />
          <Field label="Address line 1">
            <input data-testid="s-address1" value={form.address_line1 || ""} onChange={(e) => setField("address_line1", e.target.value)} />
          </Field>
          <Field label="Address line 2 (City, State ZIP)">
            <input data-testid="s-address2" value={form.address_line2 || ""} onChange={(e) => setField("address_line2", e.target.value)} />
          </Field>
          <Field label={<><Globe size={12} className="inline mr-1" /> Google Maps URL</>}>
            <input data-testid="s-maps" value={form.google_maps_url || ""} onChange={(e) => setField("google_maps_url", e.target.value)} placeholder="https://maps.google.com/..." />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Latitude">
              <input type="number" step="any" data-testid="s-lat" value={form.latitude ?? ""} onChange={(e) => setField("latitude", e.target.value === "" ? null : Number(e.target.value))} />
            </Field>
            <Field label="Longitude">
              <input type="number" step="any" data-testid="s-lng" value={form.longitude ?? ""} onChange={(e) => setField("longitude", e.target.value === "" ? null : Number(e.target.value))} />
            </Field>
          </div>
          <p className="text-[11px] text-white/40 -mt-2">Used to center the map. Find your coordinates via <a href="https://www.google.com/maps" target="_blank" rel="noreferrer" className="underline hover:text-white">Google Maps</a> — right-click your lot and copy the coordinates.</p>

          <Field label="Google Maps embed URL (recommended)">
            <textarea
              rows={3}
              data-testid="s-map-embed"
              value={form.map_embed_src || ""}
              onChange={(e) => setField("map_embed_src", e.target.value)}
              placeholder="https://www.google.com/maps/embed?pb=..."
              className="font-mono text-xs"
            />
          </Field>
          <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4 -mt-2 text-xs text-white/60 leading-relaxed space-y-2">
            <div className="text-white/80 font-semibold">How to get a working map</div>
            <ol className="list-decimal pl-4 space-y-1">
              <li>Open <a href="https://www.google.com/maps" target="_blank" rel="noreferrer" className="underline hover:text-white">Google Maps</a> and search your business.</li>
              <li>Click <b>Share</b> → <b>Embed a map</b>.</li>
              <li>Copy <b>only the URL inside</b> <code className="text-white/80">src="..."</code> from the iframe snippet.</li>
              <li>Paste it above and Save. That's it.</li>
            </ol>
            <div className="text-white/40">Leaving this empty falls back to a basic Google embed URL that Google may block via X-Frame-Options.</div>
          </div>
        </section>

        <section className="bento-cell space-y-5">
          <Header icon={Phone} label="Contact" />
          <Field label="Phone">
            <input data-testid="s-phone" value={form.phone || ""} onChange={(e) => setField("phone", e.target.value)} />
          </Field>
          <Field label={<><Mail size={12} className="inline mr-1" /> Email</>}>
            <input type="email" data-testid="s-email" value={form.email || ""} onChange={(e) => setField("email", e.target.value)} />
          </Field>
        </section>

        <section className="bento-cell space-y-5">
          <Header icon={Clock} label="Hours" />
          <Field label="Weekday hours (long)">
            <input data-testid="s-hours-weekday" value={form.hours_weekday || ""} onChange={(e) => setField("hours_weekday", e.target.value)} placeholder="Mon – Sat · 9AM – 6PM" />
          </Field>
          <Field label="Sunday hours">
            <input data-testid="s-hours-sunday" value={form.hours_sunday || ""} onChange={(e) => setField("hours_sunday", e.target.value)} placeholder="Sunday · Closed" />
          </Field>
          <Field label="Short hours (footer teaser)">
            <input data-testid="s-hours-short" value={form.hours_short || ""} onChange={(e) => setField("hours_short", e.target.value)} placeholder="9AM – 6PM Mon–Sat" />
          </Field>
        </section>

        <div className="md:col-span-2 flex gap-3">
          <button type="submit" disabled={saving} className="btn-primary" data-testid="settings-save">
            <Save size={16} /> {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </form>
    </div>
  );
}

const Header = ({ icon: Icon, label }) => (
  <div className="flex items-center gap-2">
    <Icon size={14} className="text-[var(--primary)]" />
    <span className="overline">{label}</span>
  </div>
);

const Field = ({ label, children }) => (
  <div>
    <label>{label}</label>
    {children}
  </div>
);
